<template>
  <div class="p-2 sm:p-4 text-lexend overflow-x-hidden min-h-screen bg-gray-50 dark:bg-gray-900/50">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-3 sm:mb-5 mt-1">
      <div class="animate-fadeIn w-full sm:w-auto">
        <div class="flex items-center gap-2 mb-1">
          <div class="w-8 h-1 bg-indigo-600 rounded-full"></div>
          <span class="text-[10px] sm:text-xs font-black text-indigo-600 tracking-widest">Panel de Control</span>
        </div>
        <h1 class="text-xl sm:text-3xl font-black text-gray-900 dark:text-white tracking-tight flex items-center flex-wrap gap-2">
          Nueva Devolución
          <span class="px-2 sm:px-3 py-0.5 bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-300 text-xs sm:text-sm font-black rounded-lg border border-red-200 dark:border-red-800">
            Nuevo
          </span>
        </h1>
        <p class="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mt-0.5">Jean Informatico - Sistema de Devoluciones</p>
      </div>

      <div class="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
        <div class="px-3 sm:px-4 py-2 sm:py-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl text-blue-700 dark:text-blue-300 flex items-center justify-center gap-2 text-[10px] sm:text-sm font-medium">
          <Icon icon="mdi:tag-outline" class="text-base sm:text-lg" />
          Próximo: <strong>{{ proximoNumero }}</strong>
        </div>
        <button @click="volverAListado" class="px-3 sm:px-6 py-2 sm:py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 shadow-sm hover:shadow flex items-center gap-2 justify-center">
          <Icon icon="mdi:arrow-left" class="text-base sm:text-lg" />
          <span class="hidden sm:inline">Volver al listado</span>
          <span class="sm:hidden">Volver</span>
        </button>
      </div>
    </div>

    <!-- PASO 1: SELECCIONAR ORIGEN -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden mb-3 sm:mb-4">
      <div class="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-800 px-3 sm:px-4 py-2 border-b border-gray-100 dark:border-gray-700">
        <h2 class="text-sm sm:text-lg font-semibold text-gray-800 dark:text-white flex items-center">
          <div class="p-1.5 sm:p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg mr-2 sm:mr-3">
            <Icon icon="mdi:file-search" class="text-blue-600 dark:text-blue-400 text-base sm:text-xl" />
          </div>
          1. Documento Origen
        </h2>
      </div>
      <div class="p-3 sm:p-4">
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <div>
            <label class="block text-[10px] sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
              Tipo de Origen <span class="text-red-500">*</span>
            </label>
            <select v-model="form.tipo_origen" @change="onTipoOrigenChange" class="w-full px-3 sm:px-4 py-1.5 sm:py-2 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 text-[10px] sm:text-sm text-gray-900 dark:text-white">
              <option value="">Seleccionar...</option>
              <option value="venta">🛒 Venta (Contado/Crédito)</option>
              <option value="servicio_externo">🔧 Servicio Externo (Contado/Crédito)</option>
              <option value="servicio_recepcion">🛠️ Servicio de Reparación (Entregados)</option>
            </select>
          </div>

          <div class="sm:col-span-2">
            <label class="block text-[10px] sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
              Buscar Documento <span class="text-red-500">*</span>
            </label>
            <button
              type="button"
              @click="abrirModalBusqueda"
              :disabled="!form.tipo_origen"
              :class="[
                'w-full px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl transition-all duration-200 flex items-center gap-2 text-[10px] sm:text-sm font-medium border',
                form.tipo_origen
                  ? 'bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300 hover:shadow-md'
                  : 'bg-gray-100 dark:bg-gray-700/50 border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
              ]"
            >
              <Icon icon="mdi:magnify" class="text-base sm:text-lg" />
              <span v-if="!documentoSeleccionado">Buscar {{ nombreDocumento }}...</span>
              <span v-else class="flex-1 text-left">
                <strong>{{ documentoSeleccionado.numero }}</strong> — {{ documentoSeleccionado.cliente_nombre }}
              </span>
              <Icon v-if="documentoSeleccionado" icon="mdi:check-circle" class="text-green-500 text-base sm:text-lg" />
            </button>
          </div>
        </div>

        <!-- Documento seleccionado -->
        <div v-if="documentoSeleccionado" class="mt-3 sm:mt-4 p-3 sm:p-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-gray-700/30 dark:to-gray-700/30 rounded-xl border border-green-200 dark:border-green-800/50">
          <div class="flex items-start gap-3">
            <div class="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <Icon icon="mdi:file-check" class="text-green-600 dark:text-green-400 text-xl sm:text-2xl" />
            </div>
            <div class="flex-1 min-w-0">
              <div class="flex items-start justify-between gap-2">
                <div class="flex-1">
                  <p class="font-semibold text-gray-800 dark:text-white text-sm sm:text-base">
                    {{ documentoSeleccionado.numero }}
                  </p>
                  <div class="flex flex-wrap gap-2 sm:gap-3 mt-2 text-[10px] sm:text-xs">
                    <span class="px-2 py-0.5 rounded-full font-bold"
                      :class="documentoSeleccionado.es_credito
                        ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                        : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'">
                      {{ documentoSeleccionado.es_credito ? '💳 Crédito' : '💵 Contado' }}
                    </span>
                    <span class="px-2 py-0.5 rounded-full font-bold" :class="documentoSeleccionado.estado_color">
                      {{ documentoSeleccionado.estado }}
                    </span>
                  </div>

                  <!-- Cliente -->
                  <div class="mt-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <p class="text-[10px] sm:text-xs font-bold text-gray-500 dark:text-gray-400 mb-1">👤 CLIENTE</p>
                    <p class="font-semibold text-gray-800 dark:text-white text-xs sm:text-sm">{{ documentoSeleccionado.cliente_nombre }}</p>
                    <p class="text-[10px] sm:text-xs text-gray-600 dark:text-gray-400 mt-0.5">{{ documentoSeleccionado.cliente_documento }}</p>
                    <div class="flex flex-wrap gap-3 mt-1 text-[10px] sm:text-xs text-gray-500 dark:text-gray-400">
                      <span v-if="documentoSeleccionado.cliente_telefono">📞 {{ documentoSeleccionado.cliente_telefono }}</span>
                      <span v-if="documentoSeleccionado.cliente_email">📧 {{ documentoSeleccionado.cliente_email }}</span>
                    </div>
                  </div>

                  <!-- Totales -->
                  <div class="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
                    <div class="p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                      <p class="text-[8px] sm:text-xs text-gray-500 dark:text-gray-400">Subtotal</p>
                      <p class="font-bold text-gray-800 dark:text-white text-xs sm:text-sm">${{ formatMoney(documentoSeleccionado.subtotal_usd) }}</p>
                    </div>
                    <div class="p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                      <p class="text-[8px] sm:text-xs text-gray-500 dark:text-gray-400">IVA</p>
                      <p class="font-bold text-gray-800 dark:text-white text-xs sm:text-sm">${{ formatMoney(documentoSeleccionado.iva_usd) }}</p>
                    </div>
                    <div class="p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                      <p class="text-[8px] sm:text-xs text-gray-500 dark:text-gray-400">Total</p>
                      <p class="font-bold text-indigo-600 dark:text-indigo-400 text-xs sm:text-sm">${{ formatMoney(documentoSeleccionado.total_usd) }}</p>
                    </div>
                    <div class="p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                      <p class="text-[8px] sm:text-xs text-gray-500 dark:text-gray-400">Pagado</p>
                      <p class="font-bold text-green-600 dark:text-green-400 text-xs sm:text-sm">${{ formatMoney(documentoSeleccionado.monto_pagado_usd) }}</p>
                    </div>
                  </div>

                  <!-- Tasa Original -->
                  <div v-if="documentoSeleccionado.tasa_bs_usd > 0" class="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <div class="flex justify-between items-center">
                      <span class="text-[10px] sm:text-xs text-blue-800 dark:text-blue-300 font-medium">💱 Tasa original:</span>
                      <span class="font-bold text-blue-800 dark:text-blue-300 text-xs sm:text-sm">Bs {{ formatMoney(documentoSeleccionado.tasa_bs_usd) }}</span>
                    </div>
                  </div>

                  <!-- ⭐ MÉTODOS DE PAGO ORIGINALES -->
                  <div v-if="pagosDocumento.length > 0" class="mt-2 p-3 bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-gray-700/30 dark:to-gray-700/30 rounded-lg border border-teal-200 dark:border-teal-800/50">
                    <div class="flex items-center gap-2 mb-2">
                      <Icon icon="mdi:credit-card-outline" class="text-teal-600 dark:text-teal-400 text-base sm:text-lg" />
                      <p class="text-[10px] sm:text-xs font-bold text-teal-800 dark:text-teal-300">💳 MÉTODOS DE PAGO UTILIZADOS</p>
                    </div>

                    <div v-if="metodoPagoPrincipal" class="flex justify-between items-center p-2 bg-white dark:bg-gray-800 rounded-lg border border-teal-200 dark:border-teal-700 mb-2">
                      <div class="flex items-center gap-2">
                        <Icon :icon="getIconoMetodoPago(metodoPagoPrincipal.tipo)" class="text-teal-600 dark:text-teal-400 text-base" />
                        <span class="text-xs sm:text-sm font-semibold text-gray-800 dark:text-white capitalize">
                          {{ metodoPagoPrincipal.tipo }}
                        </span>
                        <span class="px-2 py-0.5 bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 text-[9px] sm:text-xs font-bold rounded-full">
                          Principal
                        </span>
                      </div>
                      <span class="font-bold text-teal-700 dark:text-teal-300 text-xs sm:text-sm">
                        ${{ formatMoney(metodoPagoPrincipal.montoTotal) }}
                      </span>
                    </div>

                    <div class="space-y-1 max-h-[150px] overflow-y-auto custom-scrollbar">
                      <div v-for="(pago, idx) in pagosDocumento" :key="idx" 
                           class="flex justify-between items-center text-[9px] sm:text-xs p-1.5 bg-white/70 dark:bg-gray-800/70 rounded">
                        <div class="flex items-center gap-2 flex-wrap">
                          <span class="text-gray-600 dark:text-gray-400">
                            {{ formatDate(pago.fechaPago) }}
                          </span>
                          <span class="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded font-medium capitalize flex items-center gap-1">
                            <Icon :icon="getIconoMetodoPago(pago.tipoPago)" class="text-xs" />
                            {{ pago.tipoPago }}
                          </span>
                          <span v-if="pago.numeroRecibo" class="text-gray-500 dark:text-gray-400">
                            #{{ pago.numeroRecibo }}
                          </span>
                        </div>
                        <span class="font-semibold text-gray-800 dark:text-white">
                          ${{ formatMoney(pago.montoUsd) }}
                        </span>
                      </div>
                    </div>

                    <div class="mt-2 pt-2 border-t border-teal-200 dark:border-teal-800 flex justify-between items-center">
                      <span class="text-[10px] sm:text-xs font-medium text-teal-800 dark:text-teal-300">Total pagado por cliente:</span>
                      <span class="font-bold text-teal-800 dark:text-teal-300 text-xs sm:text-sm">
                        ${{ formatMoney(documentoSeleccionado.monto_pagado_usd) }}
                      </span>
                    </div>
                  </div>

                  <!-- ⭐ ALERTA: Solo si es CRÉDITO con pago parcial -->
                  <div v-if="esVentaCreditoConPagoParcial" 
                       class="mt-2 p-2 sm:p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                    <div class="flex items-start gap-2">
                      <Icon icon="mdi:information-outline" class="text-amber-600 dark:text-amber-400 text-base flex-shrink-0 mt-0.5" />
                      <div class="text-amber-800 dark:text-amber-300 text-[10px] sm:text-xs">
                        <p class="font-bold mb-1">⚠️ Crédito con pago parcial</p>
                        <p class="text-[9px] sm:text-xs">
                          El cliente pagó <strong>${{ formatMoney(documentoSeleccionado.monto_pagado_usd) }}</strong> 
                          de <strong>${{ formatMoney(documentoSeleccionado.total_usd) }}</strong>.
                          Solo se devuelve la parte del cliente.
                        </p>
                      </div>
                    </div>
                  </div>

                  <!-- Saldo pendiente -->
                  <div v-if="documentoSeleccionado.es_credito && documentoSeleccionado.saldo_pendiente_usd > 0" class="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                    <div class="flex justify-between items-center">
                      <span class="text-[10px] sm:text-xs text-yellow-800 dark:text-yellow-300 font-medium">⚠️ Saldo pendiente:</span>
                      <span class="font-bold text-yellow-800 dark:text-yellow-300 text-xs sm:text-sm">${{ formatMoney(documentoSeleccionado.saldo_pendiente_usd) }}</span>
                    </div>
                  </div>

                  <!-- Cuotas -->
                  <div v-if="documentoSeleccionado.es_credito && documentoSeleccionado.cuotas?.length" class="mt-2 p-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                    <p class="text-[10px] sm:text-xs text-purple-800 dark:text-purple-300 font-bold mb-1">💳 CUOTAS ({{ documentoSeleccionado.cuotas.length }})</p>
                    <div class="grid grid-cols-3 gap-1 text-[9px] sm:text-xs">
                      <div><span class="text-gray-500 dark:text-gray-400">Pagadas:</span> <strong class="text-green-600 dark:text-green-400">{{ documentoSeleccionado.cuotas_pagadas }}</strong></div>
                      <div><span class="text-gray-500 dark:text-gray-400">Pendientes:</span> <strong class="text-yellow-600 dark:text-yellow-400">{{ documentoSeleccionado.cuotas_pendientes }}</strong></div>
                      <div><span class="text-gray-500 dark:text-gray-400">Vencidas:</span> <strong class="text-red-600 dark:text-red-400">{{ documentoSeleccionado.cuotas_vencidas }}</strong></div>
                    </div>
                  </div>
                </div>

                <button @click="deseleccionarDocumento" class="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all flex-shrink-0">
                  <Icon icon="mdi:close" class="text-lg" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- PASO 2: DETALLES -->
    <div v-if="documentoSeleccionado" class="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
      <!-- Columna izquierda -->
      <div class="space-y-3 sm:space-y-4">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div class="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-gray-800 dark:to-gray-800 px-3 sm:px-4 py-2 border-b border-gray-100 dark:border-gray-700">
            <h2 class="text-sm sm:text-lg font-semibold text-gray-800 dark:text-white flex items-center">
              <div class="p-1.5 sm:p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg mr-2 sm:mr-3">
                <Icon icon="mdi:information" class="text-purple-600 dark:text-purple-400 text-base sm:text-xl" />
              </div>
              2. Datos Generales
            </h2>
          </div>
          <div class="p-3 sm:p-4 space-y-3 sm:space-y-4">
            <div>
              <label class="block text-[10px] sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">Motivo <span class="text-red-500">*</span></label>
              <select v-model="form.motivo" class="w-full px-3 sm:px-4 py-1.5 sm:py-2 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-2 focus:ring-purple-500 text-[10px] sm:text-sm text-gray-900 dark:text-white">
                <option value="">Seleccionar...</option>
                <option value="producto_defectuoso">Producto defectuoso</option>
                <option value="error_cobro">Error de cobro</option>
                <option value="servicio_no_realizado">Servicio no realizado</option>
                <option value="cliente_arrepentido">Cliente arrepentido</option>
                <option value="garantia">Garantía</option>
                <option value="sobrepago">Sobrepago</option>
                <option value="anulacion">Anulación</option>
                <option value="otro">Otro</option>
              </select>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label class="block text-[10px] sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">Tasa Bs/USD (Original)</label>
                <div class="relative">
                  <span class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm font-semibold">Bs</span>
                  <input v-model.number="form.tasa_bs_usd" type="number" step="0.01" readonly class="w-full pl-10 pr-3 py-1.5 sm:py-2 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300 font-mono text-[10px] sm:text-sm cursor-not-allowed" />
                </div>
              </div>

              <div>
                <label class="block text-[10px] sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">Tipo de Reembolso</label>
                <select v-model="form.tipo_reembolso" class="w-full px-3 sm:px-4 py-1.5 sm:py-2 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-2 focus:ring-purple-500 text-[10px] sm:text-sm text-gray-900 dark:text-white">
                  <option value="">Seleccionar...</option>
                  <option value="efectivo">Efectivo</option>
                  <option value="transferencia">Transferencia</option>
                  <option value="credito_cuenta">Crédito en cuenta</option>
                  <option value="nota_credito">Nota de crédito</option>
                  <option value="devolucion_producto">Devolución de producto</option>
                </select>
              </div>
            </div>

            <div>
              <label class="block text-[10px] sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">Descripción</label>
              <textarea v-model="form.descripcion" rows="2" class="w-full px-3 sm:px-4 py-1.5 sm:py-2 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-2 focus:ring-purple-500 text-[10px] sm:text-sm text-gray-900 dark:text-white resize-none"></textarea>
            </div>

            <div>
              <label class="block text-[10px] sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">Observaciones</label>
              <textarea v-model="form.observaciones" rows="2" class="w-full px-3 sm:px-4 py-1.5 sm:py-2 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-2 focus:ring-purple-500 text-[10px] sm:text-sm text-gray-900 dark:text-white resize-none"></textarea>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 pt-2 border-t border-gray-100 dark:border-gray-700">
              <label class="flex items-center gap-3 p-3 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-gray-700/30 dark:to-gray-700/30 rounded-xl border border-orange-200 dark:border-orange-800/50 cursor-pointer">
                <input type="checkbox" v-model="form.afecta_inventario" class="w-4 h-4 sm:w-5 sm:h-5 text-orange-600 rounded focus:ring-orange-500" />
                <div class="flex-1">
                  <p class="text-[10px] sm:text-sm font-medium text-gray-800 dark:text-white">Afecta inventario</p>
                  <p class="text-[8px] sm:text-xs text-gray-500 dark:text-gray-400">Reingresa stock</p>
                </div>
                <Icon icon="mdi:package-variant-closed" class="text-orange-500 text-lg sm:text-xl" />
              </label>

              <label class="flex items-center gap-3 p-3 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-gray-700/30 dark:to-gray-700/30 rounded-xl border border-green-200 dark:border-green-800/50 cursor-pointer">
                <input type="checkbox" v-model="form.afecta_caja" class="w-4 h-4 sm:w-5 sm:h-5 text-green-600 rounded focus:ring-green-500" />
                <div class="flex-1">
                  <p class="text-[10px] sm:text-sm font-medium text-gray-800 dark:text-white">Afecta caja</p>
                  <p class="text-[8px] sm:text-xs text-gray-500 dark:text-gray-400">Salida de dinero</p>
                </div>
                <Icon icon="mdi:cash-remove" class="text-green-500 text-lg sm:text-xl" />
              </label>
            </div>
          </div>
        </div>
      </div>

      <!-- Columna derecha -->
      <div class="space-y-3 sm:space-y-4">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div class="bg-gradient-to-r from-orange-50 to-amber-50 dark:from-gray-800 dark:to-gray-800 px-3 sm:px-4 py-2 sm:py-3 border-b border-gray-100 dark:border-gray-700">
            <div class="flex justify-between items-center">
              <h2 class="text-sm sm:text-lg font-semibold text-gray-800 dark:text-white flex items-center">
                <div class="p-1.5 sm:p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg mr-2 sm:mr-3">
                  <Icon icon="mdi:package-variant" class="text-orange-600 dark:text-orange-400 text-base sm:text-xl" />
                </div>
                3. Items a Devolver
              </h2>
              <span class="px-2 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 text-[10px] sm:text-xs font-bold rounded-lg">
                {{ itemsSeleccionados.length }} / {{ form.detalles.length }}
              </span>
            </div>
          </div>

          <div class="p-3 sm:p-4">
            <div v-if="form.detalles.length > 0" class="space-y-2 sm:space-y-3 max-h-[500px] overflow-y-auto pr-1 sm:pr-2 custom-scrollbar">
              <div v-for="(det, idx) in form.detalles" :key="idx" :class="['p-3 sm:p-4 rounded-xl border transition-all', det.seleccionado ? 'bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 border-orange-300 dark:border-orange-700' : 'bg-gray-50 dark:bg-gray-700/30 border-gray-200 dark:border-gray-700']">
                <div class="flex items-start gap-3">
                  <input type="checkbox" v-model="det.seleccionado" class="mt-1 w-4 h-4 sm:w-5 sm:h-5 text-orange-600 rounded focus:ring-orange-500" />
                  <div class="flex-1 min-w-0">
                    <div class="flex justify-between items-start gap-2">
                      <div class="flex-1 min-w-0">
                        <p class="font-semibold text-gray-800 dark:text-white text-xs sm:text-sm truncate">{{ det.nombre }}</p>
                        <p class="text-[8px] sm:text-xs text-gray-500 dark:text-gray-400 mt-0.5">Código: {{ det.codigo || 'N/A' }}</p>
                      </div>
                      <span class="px-2 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 text-[8px] sm:text-xs font-bold rounded-lg">#{{ idx + 1 }}</span>
                    </div>

                    <div v-if="det.seleccionado" class="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mt-3">
                      <div>
                        <label class="block text-[8px] sm:text-xs text-gray-500 dark:text-gray-400 mb-0.5">Cantidad</label>
                        <input v-model.number="det.cantidad" type="number" :max="det.cantidad_original" min="1" class="w-full px-2 py-1 border border-gray-200 dark:border-gray-600 rounded-lg text-center bg-white dark:bg-gray-700 focus:ring-2 focus:ring-orange-500 text-[10px] sm:text-sm text-gray-900 dark:text-white" />
                        <p class="text-[8px] text-gray-400 mt-0.5 text-center">de {{ det.cantidad_original }}</p>
                      </div>

                      <div>
                        <label class="block text-[8px] sm:text-xs text-gray-500 dark:text-gray-400 mb-0.5">
                          Precio USD
                          <span v-if="det.factor_cliente < 1" class="text-orange-600 dark:text-orange-400 font-bold">(parte cliente)</span>
                        </label>
                        <input v-model.number="det.precio_unitario_usd" type="number" step="0.01" class="w-full px-2 py-1 border border-gray-200 dark:border-gray-600 rounded-lg text-right bg-white dark:bg-gray-700 focus:ring-2 focus:ring-orange-500 text-[10px] sm:text-sm text-gray-900 dark:text-white" />
                        <p v-if="det.factor_cliente < 1" class="text-[8px] text-orange-500 mt-0.5 text-right">
                          Original: ${{ formatMoney(det.precio_unitario_original) }}
                        </p>
                      </div>

                      <div>
                        <label class="block text-[8px] sm:text-xs text-gray-500 dark:text-gray-400 mb-0.5">Condición</label>
                        <select v-model="det.condicion_producto" class="w-full px-2 py-1 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 focus:ring-2 focus:ring-orange-500 text-[10px] sm:text-sm text-gray-900 dark:text-white">
                          <option value="bueno">Bueno</option>
                          <option value="defectuoso">Defectuoso</option>
                          <option value="dañado">Dañado</option>
                          <option value="inservible">Inservible</option>
                        </select>
                      </div>

                      <div>
                        <label class="block text-[8px] sm:text-xs text-gray-500 dark:text-gray-400 mb-0.5">Subtotal</label>
                        <div class="px-2 py-1 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-right text-[10px] sm:text-sm font-semibold text-gray-800 dark:text-white">
                          ${{ formatMoney(det.cantidad * det.precio_unitario_usd) }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="text-center py-8 sm:py-12">
              <Icon icon="mdi:package-variant-closed-remove" class="text-4xl sm:text-5xl text-gray-400 mx-auto mb-3" />
              <p class="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Este documento no tiene items</p>
            </div>
          </div>
        </div>

        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div class="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-gray-800 dark:to-gray-800 px-3 sm:px-4 py-2 sm:py-3 border-b border-gray-100 dark:border-gray-700">
            <h2 class="text-sm sm:text-lg font-semibold text-gray-800 dark:text-white flex items-center">
              <div class="p-1.5 sm:p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg mr-2 sm:mr-3">
                <Icon icon="mdi:calculator" class="text-indigo-600 dark:text-indigo-400 text-base sm:text-xl" />
              </div>
              Resumen
            </h2>
          </div>
          <div class="p-3 sm:p-4 space-y-2 sm:space-y-3">
            <div class="flex justify-between items-center py-1.5 sm:py-2">
              <span class="text-[10px] sm:text-sm text-gray-600 dark:text-gray-400">Items seleccionados:</span>
              <span class="font-semibold text-gray-800 dark:text-white text-xs sm:text-base">{{ itemsSeleccionados.length }} / {{ form.detalles.length }}</span>
            </div>
            <div class="flex justify-between items-center py-1.5 sm:py-2">
              <span class="text-[10px] sm:text-sm text-gray-600 dark:text-gray-400">Subtotal USD:</span>
              <span class="font-semibold text-gray-800 dark:text-white text-xs sm:text-base">$ {{ formatMoney(totales.subtotal_usd) }}</span>
            </div>

            <div class="border-t border-gray-200 dark:border-gray-700 pt-3 sm:pt-4 mt-1 sm:mt-2">
              <div class="flex justify-between items-center">
                <span class="font-bold text-gray-800 dark:text-white text-sm sm:text-lg">TOTAL USD:</span>
                <span class="font-bold text-xl sm:text-2xl text-indigo-600 dark:text-indigo-400">$ {{ formatMoney(totales.total_usd) }}</span>
              </div>
              <div class="flex justify-between items-center mt-2 sm:mt-3 text-[10px] sm:text-sm bg-gray-50 dark:bg-gray-700/30 p-2 sm:p-3 rounded-xl">
                <span class="text-gray-600 dark:text-gray-400">TOTAL Bs:</span>
                <span class="font-semibold text-gray-800 dark:text-white">Bs {{ formatMoney(totales.total_bs) }}</span>
              </div>
            </div>

            <div v-if="documentoSeleccionado" class="p-2 sm:p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 text-[10px] sm:text-xs">
              <div class="flex justify-between items-center text-blue-800 dark:text-blue-300 mb-1">
                <span class="font-medium">Total del documento:</span>
                <span class="font-bold">${{ formatMoney(documentoSeleccionado.total_usd) }}</span>
              </div>
              <div class="flex justify-between items-center text-blue-800 dark:text-blue-300 mb-1">
                <span class="font-medium">Pagado por cliente:</span>
                <span class="font-bold">${{ formatMoney(documentoSeleccionado.monto_pagado_usd) }}</span>
              </div>
              <div class="flex justify-between items-center text-blue-800 dark:text-blue-300 pt-1 border-t border-blue-200 dark:border-blue-800">
                <span class="font-bold">Máximo a devolver:</span>
                <span class="font-bold text-base">${{ formatMoney(montoMaximoDevolver) }}</span>
              </div>
            </div>

            <div class="pt-3 sm:pt-4 border-t border-gray-200 dark:border-gray-700">
              <div class="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <button type="button" @click="volverAListado" class="w-full sm:flex-1 px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all text-[10px] sm:text-sm font-medium">Cancelar</button>
                <button @click="guardar" :disabled="!puedeGuardar || guardando" :class="['w-full sm:flex-1 px-3 sm:px-4 py-2 sm:py-3 rounded-xl transition-all flex items-center justify-center gap-1 sm:gap-2 font-semibold text-[10px] sm:text-sm shadow-lg hover:shadow-xl', puedeGuardar && !guardando ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-700 hover:to-emerald-700' : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed']">
                  <Icon v-if="guardando" icon="mdi:loading" class="animate-spin text-base sm:text-lg" />
                  <Icon v-else icon="mdi:check-circle-outline" class="text-base sm:text-lg" />
                  {{ guardando ? 'Guardando...' : '💾 Guardar Devolución' }}
                </button>
              </div>

              <div v-if="!puedeGuardar && !guardando" class="mt-2 sm:mt-3 space-y-1">
                <p v-if="!documentoSeleccionado" class="text-[8px] sm:text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                  <Icon icon="mdi:alert-circle" class="text-sm" /> Falta: Seleccionar documento
                </p>
                <p v-else-if="!form.motivo" class="text-[8px] sm:text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                  <Icon icon="mdi:alert-circle" class="text-sm" /> Falta: Motivo
                </p>
                <p v-else-if="itemsSeleccionados.length === 0" class="text-[8px] sm:text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                  <Icon icon="mdi:alert-circle" class="text-sm" /> Falta: Seleccionar al menos un item
                </p>
                <p v-else-if="excedeMontoMaximo" class="text-[8px] sm:text-xs text-red-600 dark:text-red-400 flex items-start gap-1">
                  <Icon icon="mdi:alert-circle" class="text-sm flex-shrink-0 mt-0.5" />
                  <span>
                    El monto a devolver (<strong>${{ formatMoney(totales.total_usd) }}</strong>) 
                    supera el máximo permitido (<strong>${{ formatMoney(montoMaximoDevolver) }}</strong>).
                    <br>
                    <span class="text-[8px]">Ajusta el precio o la cantidad del item.</span>
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Estado vacío -->
    <div v-else class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 p-8 sm:p-16 text-center">
      <Icon icon="mdi:file-document-outline" class="text-6xl sm:text-8xl text-gray-300 dark:text-gray-600 mx-auto mb-4" />
      <p class="text-sm sm:text-lg text-gray-600 dark:text-gray-400 font-medium">Selecciona un tipo de origen</p>
      <p class="text-xs sm:text-sm text-gray-500 dark:text-gray-500 mt-2">Venta, Servicio Externo o Servicio de Reparación</p>
    </div>

    <!-- MODAL DE BÚSQUEDA -->
    <Teleport to="body">
      <div v-if="modalBusqueda.visible" class="fixed inset-0 z-[99999] overflow-hidden flex items-center justify-center p-2 sm:p-4" role="dialog">
        <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="cerrarModalBusqueda"></div>

        <div class="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden animate-zoomIn">
          <div class="p-3 sm:p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <div class="flex justify-between items-center">
              <h3 class="text-base sm:text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <div class="p-1.5 sm:p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                  <Icon icon="mdi:file-search" class="text-indigo-600 dark:text-indigo-400 text-lg sm:text-xl" />
                </div>
                Buscar {{ nombreDocumento }}
              </h3>
              <button @click="cerrarModalBusqueda" class="p-1.5 sm:p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl">
                <Icon icon="mdi:close" class="text-xl sm:text-2xl text-gray-500 dark:text-gray-400" />
              </button>
            </div>
            <div class="mt-3 sm:mt-4">
              <div class="relative">
                <Icon icon="mdi:magnify" class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input v-model="modalBusqueda.termino" type="text" placeholder="Buscar por número, cliente, documento..." @input="debounceBuscarDocumento" class="w-full pl-9 sm:pl-12 pr-3 sm:pr-4 py-2 sm:py-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:ring-2 focus:ring-indigo-500 text-xs sm:text-sm text-gray-900 dark:text-white dark:placeholder-gray-400" />
              </div>
            </div>
          </div>

          <div class="p-3 sm:p-6 overflow-y-auto flex-1 custom-scrollbar">
            <div v-if="modalBusqueda.cargando" class="text-center py-8 sm:py-12">
              <div class="inline-block animate-spin rounded-full h-6 sm:h-8 w-6 sm:w-8 border-3 border-indigo-500 border-t-transparent"></div>
              <p class="text-gray-500 dark:text-gray-400 mt-2 sm:mt-3 text-sm">
                Cargando {{ nombreDocumento }} y clientes...
              </p>
              <p v-if="modalBusqueda.progreso > 0" class="text-gray-400 dark:text-gray-500 mt-1 text-xs font-bold">
                {{ modalBusqueda.progreso }} registros procesados
              </p>
            </div>

            <div v-else-if="resultadosMostrados.length > 0" class="space-y-2 sm:space-y-3">
              <div v-for="doc in resultadosMostrados" :key="doc.id" @click="seleccionarDocumento(doc)" class="p-3 sm:p-4 border border-gray-200 dark:border-gray-700 rounded-xl hover:border-indigo-500 dark:hover:border-indigo-500 cursor-pointer transition-all duration-200 hover:shadow-lg bg-white dark:bg-gray-800">
                <div class="flex items-start gap-3">
                  <div class="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex-shrink-0">
                    <Icon icon="mdi:file-document" class="text-indigo-600 dark:text-indigo-400 text-lg sm:text-xl" />
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="flex justify-between items-start gap-2">
                      <div>
                        <p class="font-semibold text-gray-900 dark:text-white text-xs sm:text-sm">{{ doc.numero }}</p>
                        <p class="text-[10px] sm:text-xs text-gray-600 dark:text-gray-400 mt-0.5">{{ doc.cliente_nombre }}</p>
                        <p class="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400">{{ doc.cliente_documento }}</p>
                        <div class="flex flex-wrap items-center gap-2 sm:gap-3 mt-1 sm:mt-2 text-[9px] sm:text-xs">
                          <span class="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                            <Icon icon="mdi:calendar" /> {{ doc.fecha }}
                          </span>
                          <span class="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                            <Icon icon="mdi:package-variant" /> {{ doc.items_count }} items
                          </span>
                          <span v-if="doc.es_credito" class="flex items-center gap-1 px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded font-bold">
                            💳 Crédito
                          </span>
                          <span v-else class="flex items-center gap-1 px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded font-bold">
                            💵 Contado
                          </span>
                          <span v-if="doc.tasa_bs_usd > 0" class="flex items-center gap-1 text-blue-600 dark:text-blue-400">
                            💱 Bs {{ formatMoney(doc.tasa_bs_usd) }}
                          </span>
                        </div>
                      </div>
                      <div class="text-right flex-shrink-0">
                        <p class="font-bold text-indigo-600 dark:text-indigo-400 text-sm sm:text-base">{{ doc.total_formatted }}</p>
                        <span :class="['inline-block mt-1 px-2 py-0.5 text-[8px] sm:text-xs rounded-full font-bold', doc.estado_color]">
                          {{ doc.estado }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="text-center py-8 sm:py-12">
              <Icon icon="mdi:file-remove" class="text-4xl sm:text-5xl text-gray-400 mx-auto mb-3" />
              <p class="text-gray-600 dark:text-gray-400 text-sm">No se encontraron documentos</p>
            </div>
          </div>

          <div class="p-3 sm:p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex-shrink-0">
            <div class="flex justify-between items-center">
              <p class="text-[10px] sm:text-sm text-gray-600 dark:text-gray-400">
                <span class="font-semibold text-gray-800 dark:text-white">{{ resultadosMostrados.length }}</span> resultados
              </p>
              <button @click="cerrarModalBusqueda" class="px-4 sm:px-6 py-2 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition-all text-[10px] sm:text-sm font-medium">Cancelar</button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ⭐ SISTEMA DE NOTIFICACIONES TOAST PROFESIONAL -->
    <Teleport to="body">
      <div class="fixed top-4 right-4 z-[999999] flex flex-col gap-3 pointer-events-none max-w-sm w-full px-2">
        <TransitionGroup
          enter-active-class="transition-all duration-300 ease-out"
          enter-from-class="opacity-0 translate-x-full scale-95"
          enter-to-class="opacity-100 translate-x-0 scale-100"
          leave-active-class="transition-all duration-300 ease-in absolute"
          leave-from-class="opacity-100 translate-x-0 scale-100"
          leave-to-class="opacity-0 translate-x-full scale-95"
          move-class="transition-all duration-300"
        >
          <div
            v-for="toast in toasts"
            :key="toast.id"
            class="pointer-events-auto relative overflow-hidden rounded-2xl shadow-2xl backdrop-blur-xl border w-full"
            :class="getToastClasses(toast.type)"
          >
            <!-- Barra de progreso animada -->
            <div
              class="absolute bottom-0 left-0 h-1 rounded-full"
              :class="getToastProgressClass(toast.type)"
              :style="{ animation: `toastProgress ${toast.duration}ms linear forwards` }"
            ></div>

            <div class="flex items-start gap-3 p-4">
              <!-- Icono con animación -->
              <div
                class="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center shadow-sm"
                :class="getToastIconBg(toast.type)"
              >
                <Icon
                  :icon="getToastIcon(toast.type)"
                  class="text-2xl animate-toastIcon"
                  :class="getToastIconColor(toast.type)"
                />
              </div>

              <!-- Contenido -->
              <div class="flex-1 min-w-0">
                <p class="font-bold text-sm" :class="getToastTitleColor(toast.type)">
                  {{ toast.title }}
                </p>
                <p v-if="toast.message" class="text-xs mt-0.5 leading-relaxed" :class="getToastMessageColor(toast.type)">
                  {{ toast.message }}
                </p>
              </div>

              <!-- Botón cerrar -->
              <button
                @click="cerrarToast(toast.id)"
                class="flex-shrink-0 p-1 rounded-lg transition-all hover:scale-110"
                :class="getToastCloseClass(toast.type)"
              >
                <Icon icon="mdi:close" class="text-base" />
              </button>
            </div>
          </div>
        </TransitionGroup>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { Icon } from '@iconify/vue';
import { getCurrentUser, apiFetch } from '@/api/index';
import { crearDevolucion, previsualizarSiguienteNumero } from '@/api/devoluciones';
import { obtenerClientePorId } from '@/api/clientes.js';
import { obtenerVentas, obtenerVentaConRelaciones } from '@/api/ventas.js';
import { obtenerServicios as obtenerServiciosExternos, obtenerServicioConRelaciones } from '@/api/serviciosExternos.js';
import { obtenerRecepciones, obtenerRecepcionConRelaciones } from '@/api/recepcionEquipo.js';

// ⭐ IMPORTAR APIs DE PAGOS
import { obtenerPagosPorVenta, obtenerPagosVenta } from '@/api/pagoVenta.js';
import { obtenerPagosSalidaEquipo } from '@/api/pagoSalidaEquipo.js';

// ⭐ IMPORTAR APIs DE CRÉDITO
import { obtenerCuotasPorSalida } from '@/api/creditoSalidaEquipo.js';
import { obtenerPagosServicioExterno } from '@/api/creditoEmpresa.js';

const router = useRouter();
const usuario = getCurrentUser();

const proximoNumero = ref('...');
const guardando = ref(false);
const documentoSeleccionado = ref(null);
const pagosDocumento = ref([]);

const cacheClientes = ref({});
const cacheProductos = ref({});
const cacheServicios = ref({});

// ⭐ SISTEMA DE TOASTS
const toasts = ref([]);
let toastIdCounter = 0;

const mostrarToast = (type, title, message = '', duration = 4000) => {
  const id = ++toastIdCounter;
  toasts.value.push({ id, type, title, message, duration });

  setTimeout(() => {
    cerrarToast(id);
  }, duration);
};

const cerrarToast = (id) => {
  const index = toasts.value.findIndex(t => t.id === id);
  if (index !== -1) {
    toasts.value.splice(index, 1);
  }
};

const toastSuccess = (title, message = '') => mostrarToast('success', title, message, 4000);
const toastError = (title, message = '') => mostrarToast('error', title, message, 5000);
const toastWarning = (title, message = '') => mostrarToast('warning', title, message, 4500);
const toastInfo = (title, message = '') => mostrarToast('info', title, message, 4000);

// Helpers visuales de toast
const getToastClasses = (type) => {
  const map = {
    success: 'bg-white/95 dark:bg-gray-800/95 border-emerald-200 dark:border-emerald-800/60 shadow-emerald-500/10',
    error: 'bg-white/95 dark:bg-gray-800/95 border-red-200 dark:border-red-800/60 shadow-red-500/10',
    warning: 'bg-white/95 dark:bg-gray-800/95 border-amber-200 dark:border-amber-800/60 shadow-amber-500/10',
    info: 'bg-white/95 dark:bg-gray-800/95 border-blue-200 dark:border-blue-800/60 shadow-blue-500/10'
  };
  return map[type] || map.info;
};

const getToastProgressClass = (type) => {
  const map = {
    success: 'bg-gradient-to-r from-emerald-400 to-green-500',
    error: 'bg-gradient-to-r from-red-400 to-rose-500',
    warning: 'bg-gradient-to-r from-amber-400 to-orange-500',
    info: 'bg-gradient-to-r from-blue-400 to-indigo-500'
  };
  return map[type] || map.info;
};

const getToastIconBg = (type) => {
  const map = {
    success: 'bg-gradient-to-br from-emerald-100 to-green-100 dark:from-emerald-900/40 dark:to-green-900/40',
    error: 'bg-gradient-to-br from-red-100 to-rose-100 dark:from-red-900/40 dark:to-rose-900/40',
    warning: 'bg-gradient-to-br from-amber-100 to-orange-100 dark:from-amber-900/40 dark:to-orange-900/40',
    info: 'bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/40 dark:to-indigo-900/40'
  };
  return map[type] || map.info;
};

const getToastIcon = (type) => {
  const map = {
    success: 'mdi:check-circle',
    error: 'mdi:alert-circle',
    warning: 'mdi:alert',
    info: 'mdi:information'
  };
  return map[type] || map.info;
};

const getToastIconColor = (type) => {
  const map = {
    success: 'text-emerald-600 dark:text-emerald-400',
    error: 'text-red-600 dark:text-red-400',
    warning: 'text-amber-600 dark:text-amber-400',
    info: 'text-blue-600 dark:text-blue-400'
  };
  return map[type] || map.info;
};

const getToastTitleColor = (type) => {
  const map = {
    success: 'text-emerald-900 dark:text-emerald-100',
    error: 'text-red-900 dark:text-red-100',
    warning: 'text-amber-900 dark:text-amber-100',
    info: 'text-blue-900 dark:text-blue-100'
  };
  return map[type] || map.info;
};

const getToastMessageColor = (type) => {
  const map = {
    success: 'text-emerald-700 dark:text-emerald-300',
    error: 'text-red-700 dark:text-red-300',
    warning: 'text-amber-700 dark:text-amber-300',
    info: 'text-blue-700 dark:text-blue-300'
  };
  return map[type] || map.info;
};

const getToastCloseClass = (type) => {
  const map = {
    success: 'text-emerald-500 hover:bg-emerald-100 dark:hover:bg-emerald-900/30',
    error: 'text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30',
    warning: 'text-amber-500 hover:bg-amber-100 dark:hover:bg-amber-900/30',
    info: 'text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-900/30'
  };
  return map[type] || map.info;
};

const form = ref({
  tipo_origen: '',
  motivo: '',
  idCliente: null,
  tasa_bs_usd: 0,
  tipo_reembolso: '',
  descripcion: '',
  observaciones: '',
  afecta_inventario: false,
  afecta_caja: false,
  detalles: []
});

const modalBusqueda = ref({
  visible: false,
  termino: '',
  cargando: false,
  resultados: [],
  progreso: 0
});

// =====================================================
// COMPUTED
// =====================================================
const nombreDocumento = computed(() => {
  const tipos = {
    venta: 'Ventas',
    servicio_externo: 'Servicios Externos',
    servicio_recepcion: 'Servicios de Reparación'
  };
  return tipos[form.value.tipo_origen] || 'Documento';
});

const resultadosMostrados = computed(() => {
  const termino = modalBusqueda.value.termino?.trim().toLowerCase();
  if (!termino || termino.length < 2) {
    return modalBusqueda.value.resultados;
  }
  return modalBusqueda.value.resultados.filter(d =>
    d.numero?.toLowerCase().includes(termino) ||
    d.cliente_nombre?.toLowerCase().includes(termino) ||
    d.cliente_documento?.toLowerCase().includes(termino) ||
    d.cliente_telefono?.toLowerCase().includes(termino)
  );
});

const itemsSeleccionados = computed(() => form.value.detalles.filter(d => d.seleccionado));

const totales = computed(() => {
  let subtotal_usd = 0;
  for (const d of itemsSeleccionados.value) {
    subtotal_usd += (d.cantidad || 0) * (d.precio_unitario_usd || 0);
  }
  const total_usd = subtotal_usd;
  return { subtotal_usd, iva_usd: 0, total_usd, total_bs: total_usd * (form.value.tasa_bs_usd || 0) };
});

const montoMaximoDevolver = computed(() => {
  if (!documentoSeleccionado.value) return 0;
  if (!documentoSeleccionado.value.es_credito) {
    return documentoSeleccionado.value.total_usd || 0;
  }
  return documentoSeleccionado.value.monto_pagado_usd || 0;
});

const excedeMontoMaximo = computed(() => {
  return totales.value.total_usd > montoMaximoDevolver.value + 0.01;
});

const puedeGuardar = computed(() => {
  const base = documentoSeleccionado.value 
    && form.value.motivo 
    && form.value.tasa_bs_usd > 0 
    && itemsSeleccionados.value.length > 0;
  
  if (!base) return false;
  if (excedeMontoMaximo.value) return false;
  return true;
});

const metodoPagoPrincipal = computed(() => {
  if (!pagosDocumento.value.length) return null;
  
  const agrupado = {};
  pagosDocumento.value.forEach(p => {
    const tipo = p.tipoPago || 'No especificado';
    if (!agrupado[tipo]) {
      agrupado[tipo] = { tipo, montoTotal: 0, cantidad: 0 };
    }
    agrupado[tipo].montoTotal += p.montoUsd || 0;
    agrupado[tipo].cantidad += 1;
  });

  return Object.values(agrupado).sort((a, b) => b.montoTotal - a.montoTotal)[0];
});

const esVentaCreditoConPagoParcial = computed(() => {
  if (!documentoSeleccionado.value) return false;
  if (!documentoSeleccionado.value.es_credito) return false;
  const pagado = documentoSeleccionado.value.monto_pagado_usd || 0;
  const total = documentoSeleccionado.value.total_usd || 0;
  return pagado > 0 && pagado < total - 0.01;
});

// =====================================================
// HELPERS
// =====================================================
const normalizar = (str) => {
  if (!str) return '';
  return str.toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[_\s-]+/g, '').trim();
};

const esEntregado = (estado) => {
  const e = normalizar(estado);
  return e === 'entregado' || e === 'entregada' || e === 'finalizado' || e === 'finalizada' || e === 'completado' || e === 'completada' || e === 'terminado';
};

const detectarEsCredito = (v) => {
  const tipoVenta = (v.tipoVenta || v.tipo_venta || v.TipoVenta || v.tipo || '').toString().toLowerCase().trim();
  const formaPago = (v.formaPago || v.forma_pago || v.FormaPago || '').toString().toLowerCase().trim();
  const metodoPago = (v.metodoPago || v.metodo_pago || v.MetodoPago || '').toString().toLowerCase().trim();
  
  const normalizarStr = (s) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const tipoNorm = normalizarStr(tipoVenta);
  const formaNorm = normalizarStr(formaPago);
  const metodoNorm = normalizarStr(metodoPago);
  
  const palabrasCredito = ['credito', 'cashea', 'financia', 'empresa', 'abono'];
  
  const esCreditoPorTipo = palabrasCredito.some(p => tipoNorm.includes(p));
  const esCreditoPorForma = palabrasCredito.some(p => formaNorm.includes(p));
  const esCreditoPorMetodo = palabrasCredito.some(p => metodoNorm.includes(p));
  
  const tieneIdTipoCredito = v.idTipoCredito && v.idTipoCredito > 0;
  const tieneCuotas = (v.cuotasCredito && v.cuotasCredito.length > 0);
  const tieneSaldoPendiente = (v.saldoPendienteUsd || v.saldoPendiente || 0) > 0.01;
  
  return esCreditoPorTipo 
      || esCreditoPorForma 
      || esCreditoPorMetodo 
      || tieneIdTipoCredito 
      || tieneCuotas 
      || tieneSaldoPendiente;
};

const detectarEsCreditoServicio = (s) => {
  const metodoPago = (s.metodoPago || s.metodo_pago || s.MetodoPago || '').toString().toLowerCase().trim();
  const formaPago = (s.formaPago || s.forma_pago || s.FormaPago || '').toString().toLowerCase().trim();
  const estadoCredito = (s.estadoCredito || s.estado_credito || s.EstadoCredito || '').toString().toLowerCase().trim();
  
  const normalizarStr = (str) => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const metodoNorm = normalizarStr(metodoPago);
  const formaNorm = normalizarStr(formaPago);
  const estadoNorm = normalizarStr(estadoCredito);
  
  const palabrasCredito = ['credito', 'cashea', 'financia', 'empresa', 'abono', 'cuotas'];
  const esCreditoPorMetodo = palabrasCredito.some(p => metodoNorm.includes(p));
  const esCreditoPorForma = palabrasCredito.some(p => formaNorm.includes(p));
  
  const tieneIdTipoCredito = s.idTipoCredito && s.idTipoCredito > 0;
  const tieneCreditos = (s.creditoServicioExternos && s.creditoServicioExternos.length > 0)
                      || (s.creditos && s.creditos.length > 0);
  const tieneSaldoPendiente = (s.saldoPendienteUsd || s.saldoPendiente || 0) > 0.01;
  const estadoEsCredito = estadoNorm && estadoNorm !== 'pagado' && estadoNorm !== 'completado';
  
  return esCreditoPorMetodo ||
    esCreditoPorForma ||
    tieneIdTipoCredito ||
    tieneCreditos ||
    tieneSaldoPendiente ||
    estadoEsCredito;
};

const detectarEsCreditoRecepcion = (r) => {
  const tieneSaldoPendiente = (r.saldoPendienteUsd || r.saldo_pendiente || 0) > 0.01;
  const montoPagado = (r.montoPagadoDiagnosticoUsd || 0) + (r.montoPagadoReparacionUsd || 0);
  const total = r.precioTotalUsd || r.totalUsd || 0;
  const pagadoMenosQueTotal = montoPagado > 0 && montoPagado < total - 0.01;
  
  let tieneCuotas = false;
  if (r.detalles && r.detalles.length > 0) {
    for (const det of r.detalles) {
      const salidas = det.salidasEquipoDetalle || det.salidaEquipoDetalles || det.salidaEquipoDetalle || [];
      for (const salida of salidas) {
        if (salida.creditosSalidaEquipo?.length > 0 || salida.creditoSalidaEquipo?.length > 0) {
          tieneCuotas = true;
          break;
        }
      }
      if (tieneCuotas) break;
    }
  }
  
  return tieneSaldoPendiente || pagadoMenosQueTotal || tieneCuotas;
};

const detectarEsCreditoRecepcionConCuotas = async (idRecepcion, detalle) => {
  try {
    const detectadoBasico = detectarEsCreditoRecepcion(detalle || {});
    if (detectadoBasico) return true;
    
    const detalles = detalle?.detalles || detalle?.recepcionEquipoDetalles || detalle?.recepcionEquipoDetalle || [];
    
    for (const det of detalles) {
      const salidas = det.salidasEquipoDetalle || det.salidaEquipoDetalles || det.salidaEquipoDetalle || [];
      
      for (const salida of salidas) {
        const idSalidaDetalle = salida.idSalidaEquipoDetalle || salida.IdSalidaEquipoDetalle;
        if (!idSalidaDetalle) continue;
        
        try {
          const cuotasRes = await obtenerCuotasPorSalida(idSalidaDetalle);
          const cuotas = Array.isArray(cuotasRes) ? cuotasRes : (cuotasRes?.data || cuotasRes);
          
          if (Array.isArray(cuotas) && cuotas.length > 0) {
            return true;
          }
        } catch (e) { /* ignore */ }
      }
    }
    
    return false;
  } catch (e) {
    return false;
  }
};

const obtenerNombreProducto = async (idProducto) => {
  if (!idProducto) return null;
  if (cacheProductos.value[idProducto]) return cacheProductos.value[idProducto].nombre;
  try {
    const res = await apiFetch(`/Producto/${idProducto}`);
    const prod = res?.data || res;
    const nombre = prod?.nombre || null;
    const codigo = prod?.codigo || '';
    if (nombre) cacheProductos.value[idProducto] = { nombre, codigo };
    return nombre;
  } catch (e) { return null; }
};

const obtenerNombreServicio = async (idServicio) => {
  if (!idServicio) return null;
  if (cacheServicios.value[idServicio]) return cacheServicios.value[idServicio].nombre;
  try {
    const res = await apiFetch(`/Servicio/${idServicio}`);
    const serv = res?.data || res;
    const nombre = serv?.nombre || null;
    const codigo = serv?.codigo || '';
    if (nombre) cacheServicios.value[idServicio] = { nombre, codigo };
    return nombre;
  } catch (e) { return null; }
};

const getIconoMetodoPago = (tipo) => {
  if (!tipo) return 'mdi:cash';
  const t = tipo.toLowerCase();
  if (t.includes('efectivo')) return 'mdi:cash';
  if (t.includes('transferencia')) return 'mdi:bank-transfer';
  if (t.includes('tarjeta')) return 'mdi:credit-card';
  if (t.includes('punto')) return 'mdi:credit-card-outline';
  if (t.includes('credito') || t.includes('crédito')) return 'mdi:credit-card-clock';
  if (t.includes('mixto')) return 'mdi:cash-multiple';
  if (t.includes('zelle')) return 'mdi:alpha-z-circle';
  if (t.includes('paypal')) return 'mdi:paypal';
  if (t.includes('pago movil') || t.includes('pago_movil')) return 'mdi:cellphone';
  if (t.includes('abono')) return 'mdi:cash-plus';
  return 'mdi:cash';
};

const cargarEnParalelo = async (items, mapFn, concurrencia = 20, onProgress = null) => {
  const results = [];
  let index = 0;
  let procesados = 0;

  const worker = async () => {
    while (index < items.length) {
      const myIndex = index++;
      const item = items[myIndex];
      try {
        const mapped = await mapFn(item);
        results[myIndex] = mapped;
      } catch (e) {
        results[myIndex] = null;
      }
      procesados++;
      if (onProgress && procesados % 20 === 0) {
        onProgress(procesados);
      }
    }
  };

  const workers = Array(Math.min(concurrencia, items.length)).fill().map(() => worker());
  await Promise.all(workers);

  if (onProgress) onProgress(items.length);
  return results.filter(Boolean);
};

// =====================================================
// MÉTODOS
// =====================================================
const cargarPreviewNumero = async () => {
  try {
    const res = await previsualizarSiguienteNumero('DEV-');
    proximoNumero.value = res?.proximo_numero || 'DEV-....';
  } catch (e) { proximoNumero.value = 'DEV-....'; }
};

const onTipoOrigenChange = () => {
  documentoSeleccionado.value = null;
  form.value.detalles = [];
  pagosDocumento.value = [];
  if (form.value.tipo_origen) abrirModalBusqueda();
};

const abrirModalBusqueda = () => {
  if (!form.value.tipo_origen) return;
  modalBusqueda.value.visible = true;
  modalBusqueda.value.termino = '';
  modalBusqueda.value.resultados = [];
  modalBusqueda.value.progreso = 0;
  cargarDocumentos();
};

const cerrarModalBusqueda = () => modalBusqueda.value.visible = false;

const cargarDocumentos = async () => {
  modalBusqueda.value.cargando = true;
  modalBusqueda.value.progreso = 0;
  try {
    const docs = await cargarDocumentosRapido();
    modalBusqueda.value.resultados = docs;
  } catch (e) {
    console.error('Error:', e);
    modalBusqueda.value.resultados = [];
    toastError('Error al cargar', 'No se pudieron obtener los documentos. Intenta nuevamente.');
  } finally {
    modalBusqueda.value.cargando = false;
  }
};

const cargarDocumentosRapido = async () => {
  const onProgress = (n) => { modalBusqueda.value.progreso = n; };

  if (form.value.tipo_origen === 'venta') {
    const res = await obtenerVentas();
    const data = Array.isArray(res) ? res : (res.data || []);

    return await cargarEnParalelo(
      data,
      async (v) => {
        let cliente = null;
        if (v.idCliente) {
          if (cacheClientes.value[v.idCliente]) {
            cliente = cacheClientes.value[v.idCliente];
          } else {
            try {
              const cRes = await obtenerClientePorId(v.idCliente);
              cliente = cRes.success ? cRes.data : null;
              cacheClientes.value[v.idCliente] = cliente;
            } catch (e) { /* ignore */ }
          }
        }
        return mapVentaRapido(v, cliente);
      },
      20,
      onProgress
    );
  }

  if (form.value.tipo_origen === 'servicio_externo') {
    const res = await obtenerServiciosExternos();
    const data = res.success ? res.data : (Array.isArray(res) ? res : []);

    return await cargarEnParalelo(
      data,
      async (s) => {
        let cliente = null;
        if (s.idCliente) {
          if (cacheClientes.value[s.idCliente]) {
            cliente = cacheClientes.value[s.idCliente];
          } else {
            try {
              const cRes = await obtenerClientePorId(s.idCliente);
              cliente = cRes.success ? cRes.data : null;
              cacheClientes.value[s.idCliente] = cliente;
            } catch (e) { /* ignore */ }
          }
        }
        return mapServicioExternoRapido(s, cliente);
      },
      20,
      onProgress
    );
  }

  if (form.value.tipo_origen === 'servicio_recepcion') {
    const res = await obtenerRecepciones();
    const data = res.success ? res.data : (Array.isArray(res) ? res : []);
    const soloEntregados = data.filter(r => esEntregado(r.nombreEstado || r.NombreEstado));

    let pagosPorSalida = {};
    let pagosPorOrden = {};
    try {
      const pagosRes = await apiFetch('/PagoSalidaEquipo');
      const pagos = Array.isArray(pagosRes) ? pagosRes : (pagosRes?.data || []);

      pagos.forEach(p => {
        const idSalida = p.idSalidaEquipoDetalle || p.IdSalidaEquipoDetalle;
        const tasa = p.tasaBsUsd ?? p.TasaBsUsd ?? p.tasa_bs_usd ?? 0;
        if (idSalida && parseFloat(tasa) > 0) {
          pagosPorSalida[String(idSalida)] = parseFloat(tasa);
        }
        const numOrden = String(p.numeroOrden || '').trim();
        if (numOrden && parseFloat(tasa) > 0) {
          pagosPorOrden[numOrden] = parseFloat(tasa);
        }
      });
    } catch (e) { /* ignore */ }

    return await cargarEnParalelo(
      soloEntregados,
      async (r) => {
        let cliente = null;
        if (r.idCliente) {
          if (cacheClientes.value[r.idCliente]) {
            cliente = cacheClientes.value[r.idCliente];
          } else {
            try {
              const cRes = await obtenerClientePorId(r.idCliente);
              cliente = cRes.success ? cRes.data : null;
              cacheClientes.value[r.idCliente] = cliente;
            } catch (e) { /* ignore */ }
          }
        }
        return mapRecepcionRapido(r, cliente, pagosPorSalida, pagosPorOrden);
      },
      20,
      onProgress
    );
  }

  return [];
};

const mapVentaRapido = (v, cliente = null) => {
  const subtotal = v.subtotalUsd || 0;
  const total = v.totalUsd || 0;
  const factorProporcion = subtotal > 0 ? (total / subtotal) : 1;

  const clienteNombre = cliente
    ? `${cliente.nombre || ''} ${cliente.apellido || ''}`.trim()
    : (v.clienteNombre || v.nombreCliente || 'Sin cliente');
  const clienteDoc = cliente?.documento
    ? `${(cliente.tipoDocumento || 'cedula').toUpperCase()}: ${cliente.documento}`
    : (v.clienteDocumento || '');

  const esCreditoDetectado = detectarEsCredito(v);

  return {
    id: v.idVenta, tipo_origen: 'venta',
    numero: v.numeroFactura || `Venta #${v.idVenta}`,
    cliente_nombre: clienteNombre,
    cliente_documento: clienteDoc,
    cliente_telefono: cliente?.telefono || v.clienteTelefono || '',
    cliente_email: cliente?.email || v.clienteEmail || '',
    idCliente: v.idCliente,
    fecha: formatDate(v.fechaVenta || v.fechaCreacion),
    total_usd: total, total_formatted: `$${formatMoney(total)}`,
    subtotal_usd: subtotal, iva_usd: v.ivaUsd || 0,
    tasa_bs_usd: v.tasaBsUsd || 0,
    es_credito: esCreditoDetectado,
    monto_pagado_usd: v.montoPagado || 0,
    saldo_pendiente_usd: v.saldoPendienteUsd || 0,
    cuotas: v.cuotasCredito || [],
    cuotas_pagadas: 0, cuotas_pendientes: 0, cuotas_vencidas: 0,
    estado: v.estado || 'pendiente',
    estado_color: getEstadoColor(v.estado),
    items_count: v.cantidadTotal || 0,
    _factor_proporcion: factorProporcion, _raw: v
  };
};

const mapServicioExternoRapido = (s, cliente = null) => {
  const subtotal = s.subtotalUsd || 0;
  const total = s.totalUsd || s.costo || 0;
  const factorProporcion = subtotal > 0 ? (total / subtotal) : 1;

  const esCreditoDetectado = detectarEsCreditoServicio(s);

  const clienteNombre = cliente
    ? `${cliente.nombre || ''} ${cliente.apellido || ''}`.trim()
    : (s.clienteNombre || s.nombreCliente || 'Sin cliente');
  const clienteDoc = cliente?.documento
    ? `${(cliente.tipoDocumento || 'cedula').toUpperCase()}: ${cliente.documento}`
    : (s.clienteDocumento || '');

  return {
    id: s.idServicioExterno, tipo_origen: 'servicio_externo',
    numero: s.numeroServicio || s.numeroFactura || `Serv. Ext. #${s.idServicioExterno}`,
    cliente_nombre: clienteNombre,
    cliente_documento: clienteDoc,
    cliente_telefono: cliente?.telefono || s.clienteTelefono || '',
    cliente_email: cliente?.email || s.clienteEmail || '',
    idCliente: s.idCliente,
    fecha: formatDate(s.fechaProgramada || s.fechaCreacion),
    total_usd: total, total_formatted: `$${formatMoney(total)}`,
    subtotal_usd: subtotal, iva_usd: s.ivaUsd || 0,
    tasa_bs_usd: s.tasaBsUsd || 0,
    es_credito: esCreditoDetectado,
    monto_pagado_usd: s.montoPagado || 0,
    saldo_pendiente_usd: s.saldoPendienteUsd || 0,
    cuotas: s.creditoServicioExternos || [],
    cuotas_pagadas: 0, cuotas_pendientes: 0, cuotas_vencidas: 0,
    estado: s.estado || 'PENDIENTE',
    estado_color: getEstadoColor(s.estado),
    items_count: (s.detalles || []).length,
    _factor_proporcion: factorProporcion, _raw: s
  };
};

const mapRecepcionRapido = (r, cliente = null, pagosPorSalida = {}, pagosPorOrden = {}) => {
  const subtotal = r.subtotalUsd || 0;
  const total = r.precioTotalUsd || r.totalUsd || 0;
  const factorProporcion = subtotal > 0 ? (total / subtotal) : 1;
  const montoPagado = (r.montoPagadoDiagnosticoUsd || 0) + (r.montoPagadoReparacionUsd || 0);

  let tasaEncontrada = r.tasaBsUsd || r.tasa_bs_usd || 0;

  if (tasaEncontrada === 0) {
    const numOrden = String(r.numeroOrden || '').trim();
    if (numOrden && pagosPorOrden[numOrden]) {
      tasaEncontrada = pagosPorOrden[numOrden];
    }
  }

  if (tasaEncontrada === 0 && r.detalles && r.detalles.length > 0) {
    for (const det of r.detalles) {
      const salidas = det.salidasEquipoDetalle || det.salidaEquipoDetalles || det.salidaEquipoDetalle || [];
      for (const salida of salidas) {
        const idSalida = salida.idSalidaEquipoDetalle || salida.IdSalidaEquipoDetalle;
        if (idSalida && pagosPorSalida[String(idSalida)]) {
          tasaEncontrada = pagosPorSalida[String(idSalida)];
          break;
        }
      }
      if (tasaEncontrada > 0) break;
    }
  }

  const clienteNombre = cliente
    ? `${cliente.nombre || ''} ${cliente.apellido || ''}`.trim()
    : (r.nombreCliente || 'Sin cliente');
  const clienteDoc = cliente?.documento
    ? `${(cliente.tipoDocumento || 'cedula').toUpperCase()}: ${cliente.documento}`
    : (r.documentoCliente ? `CEDULA: ${r.documentoCliente}` : '');

  const esCreditoDetectado = detectarEsCreditoRecepcion(r);

  return {
    id: r.idRecepcionEquipo, tipo_origen: 'servicio_recepcion',
    numero: r.numeroOrden || `Orden #${r.idRecepcionEquipo}`,
    cliente_nombre: clienteNombre,
    cliente_documento: clienteDoc,
    cliente_telefono: cliente?.telefono || r.telefonoCliente || '',
    cliente_email: cliente?.email || '',
    idCliente: r.idCliente,
    fecha: formatDate(r.fechaIngreso),
    total_usd: total, total_formatted: `$${formatMoney(total)}`,
    subtotal_usd: subtotal || total, iva_usd: r.ivaUsd || 0,
    tasa_bs_usd: tasaEncontrada,
    es_credito: esCreditoDetectado,
    monto_pagado_usd: montoPagado || 0,
    saldo_pendiente_usd: Math.max(0, total - montoPagado),
    cuotas: [], cuotas_pagadas: 0, cuotas_pendientes: 0, cuotas_vencidas: 0,
    estado: r.nombreEstado || 'ENTREGADO',
    estado_color: getEstadoColor(r.nombreEstado),
    items_count: (r.detalles || []).length || 1,
    _factor_proporcion: factorProporcion, _raw: r
  };
};

const obtenerPagosDelDocumento = async (idDocumento, tipoOrigen) => {
  try {
    let pagos = [];

    if (tipoOrigen === 'venta') {
      const res = await obtenerPagosPorVenta(idDocumento);
      pagos = res.success ? res.data : [];
    } 
    else if (tipoOrigen === 'servicio_externo') {
      try {
        const res = await obtenerPagosServicioExterno(idDocumento);
        pagos = res.success ? res.data : (Array.isArray(res) ? res : []);
      } catch (e) {
        pagos = [];
      }
    } 
    else if (tipoOrigen === 'servicio_recepcion') {
      const res = await obtenerPagosSalidaEquipo();
      const todos = res.success ? res.data : (Array.isArray(res) ? res : []);
      pagos = todos.filter(p => 
        String(p.idRecepcionEquipo || p.IdRecepcionEquipo || '') === String(idDocumento)
      );
    }

    return pagos.map(p => ({
      id: p.idPagoVenta || p.idPagoSalidaEquipo || p.idPagoServicioExterno || p.idPago || p.id,
      montoUsd: p.montoUsd || 0,
      montoBs: p.montoBs || 0,
      tipoPago: p.tipoPago || p.metodoPago || p.tipo_pago || 'No especificado',
      fechaPago: p.fechaPago || p.fecha_creacion || p.fechaCreacion,
      numeroRecibo: p.numeroRecibo || p.numero_recibo || p.referencia || '',
      observaciones: p.observaciones || ''
    }));
  } catch (e) {
    return [];
  }
};

const debounceBuscarDocumento = (() => {
  let timeout;
  return () => {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      if (modalBusqueda.value.resultados.length === 0) {
        cargarDocumentos();
      }
    }, 200);
  };
})();

const obtenerTasaOriginalRecepcion = async (idRecepcionEquipo, todosPagos = null) => {
  try {
    const resRel = await obtenerRecepcionConRelaciones(idRecepcionEquipo);
    if (!resRel.success || !resRel.data) return 0;
    const detalle = resRel.data;

    const nombresTasa = ['tasaBsUsd', 'TasaBsUsd', 'tasa_bs_usd', 'tasaBsUSD', 'tasaCambio', 'TasaCambio', 'tasa_cambio', 'tasaVenta', 'TasaVenta', 'tasa_venta', 'precio_usd_bs', 'PrecioUsdBs', 'precioUsdBs', 'tasa', 'Tasa'];

    const extraerTasa = (obj) => {
      if (!obj || typeof obj !== 'object') return 0;
      for (const nombre of nombresTasa) {
        const valor = obj[nombre];
        if (valor != null && parseFloat(valor) > 0) return parseFloat(valor);
      }
      return 0;
    };

    let tasa = extraerTasa(detalle);
    if (tasa > 0) return tasa;

    let pagos = todosPagos;
    if (!pagos || pagos.length === 0) {
      try {
        const pagosRes = await apiFetch('/PagoSalidaEquipo');
        pagos = Array.isArray(pagosRes) ? pagosRes : (pagosRes?.data || []);
      } catch (e) { pagos = []; }
    }

    const numOrden = String(detalle.numeroOrden || detalle.NumeroOrden || '').trim();
    if (numOrden) {
      const pagoPorOrden = pagos.find(p => String(p.numeroOrden || '').trim() === numOrden);
      if (pagoPorOrden) {
        tasa = extraerTasa(pagoPorOrden);
        if (tasa > 0) return tasa;
      }
    }

    const detalles = detalle.detalles || detalle.recepcionEquipoDetalles || detalle.recepcionEquipoDetalle || [];
    for (const det of detalles) {
      const salidas = det.salidasEquipoDetalle || det.salidaEquipoDetalles || det.salidaEquipoDetalle || [];
      for (const salida of salidas) {
        const idSalida = salida.idSalidaEquipoDetalle || salida.IdSalidaEquipoDetalle;
        if (!idSalida) continue;
        const pago = pagos.find(p => String(p.idSalidaEquipoDetalle || p.IdSalidaEquipoDetalle) === String(idSalida));
        if (pago) {
          tasa = extraerTasa(pago);
          if (tasa > 0) return tasa;
        }
      }
    }

    return 0;
  } catch (e) {
    return 0;
  }
};

const seleccionarDocumento = async (doc) => {
  modalBusqueda.value.cargando = true;
  try {
    let detalle = doc._raw;

    try {
      if (form.value.tipo_origen === 'venta') {
        const res = await obtenerVentaConRelaciones(doc.id);
        detalle = res.success ? res.data : (res.data || res);
      } else if (form.value.tipo_origen === 'servicio_externo') {
        const res = await obtenerServicioConRelaciones(doc.id);
        detalle = res.success ? res.data : (res.data || res);
      } else if (form.value.tipo_origen === 'servicio_recepcion') {
        const res = await obtenerRecepcionConRelaciones(doc.id);
        detalle = res.success ? res.data : (res.data || res);
      }
    } catch (e) { /* ignore */ }

    const items = await extraerItems(detalle, form.value.tipo_origen);
    const cuotas = extraerCuotas(detalle, form.value.tipo_origen);
    const pagos = extraerPagos(detalle, form.value.tipo_origen);
    const montoPagado = pagos.reduce((sum, p) => sum + (p.montoUsd || 0), 0);

    let clienteCompleto = null;
    const idCli = detalle.idCliente || doc.idCliente;
    if (idCli) {
      if (cacheClientes.value[idCli]) {
        clienteCompleto = cacheClientes.value[idCli];
      } else {
        try {
          const cRes = await obtenerClientePorId(idCli);
          clienteCompleto = cRes.success ? cRes.data : null;
          cacheClientes.value[idCli] = clienteCompleto;
        } catch (e) { /* ignore */ }
      }
    }

    let clienteInfo = {
      nombre_completo: doc.cliente_nombre,
      documento_formateado: doc.cliente_documento,
      telefono: doc.cliente_telefono,
      email: doc.cliente_email,
      idCliente: doc.idCliente
    };

    if (clienteCompleto) {
      clienteInfo = {
        nombre_completo: `${clienteCompleto.nombre || ''} ${clienteCompleto.apellido || ''}`.trim() || doc.cliente_nombre,
        documento_formateado: clienteCompleto.documento ? `${(clienteCompleto.tipoDocumento || 'cedula').toUpperCase()}: ${clienteCompleto.documento}` : doc.cliente_documento,
        telefono: clienteCompleto.telefono || doc.cliente_telefono,
        email: clienteCompleto.email || doc.cliente_email,
        idCliente: clienteCompleto.idCliente
      };
    }

    let tasaOriginal = doc.tasa_bs_usd || 0;

    if (tasaOriginal === 0 && form.value.tipo_origen === 'servicio_recepcion') {
      tasaOriginal = await obtenerTasaOriginalRecepcion(doc.id);
    } else if (tasaOriginal === 0) {
      tasaOriginal = detalle.tasaBsUsd || detalle.tasa_bs_usd || 0;
    }

    let esCreditoFinal = doc.es_credito;
    if (form.value.tipo_origen === 'venta') {
      esCreditoFinal = detectarEsCredito(detalle);
    } else if (form.value.tipo_origen === 'servicio_externo') {
      esCreditoFinal = detectarEsCreditoServicio(detalle);
    } else if (form.value.tipo_origen === 'servicio_recepcion') {
      esCreditoFinal = await detectarEsCreditoRecepcionConCuotas(doc.id, detalle);
    }

    const montoPagadoReal = montoPagado > 0 
      ? montoPagado 
      : (doc.monto_pagado_usd || 0);

    documentoSeleccionado.value = {
      ...doc,
      es_credito: esCreditoFinal,
      cliente_nombre: clienteInfo.nombre_completo,
      cliente_documento: clienteInfo.documento_formateado,
      cliente_telefono: clienteInfo.telefono,
      cliente_email: clienteInfo.email,
      monto_pagado_usd: montoPagadoReal,
      tasa_bs_usd: tasaOriginal,
      cuotas,
      cuotas_pagadas: cuotas.filter(c => c.estado === 'pagada').length,
      cuotas_pendientes: cuotas.filter(c => c.estado === 'pendiente').length,
      cuotas_vencidas: cuotas.filter(c => c.estado === 'vencida').length,
      items
    };

    const pagosDelDoc = await obtenerPagosDelDocumento(doc.id, form.value.tipo_origen);
    
    if (pagosDelDoc.length === 0 && pagos.length > 0) {
      pagosDocumento.value = pagos.map(p => ({
        id: p.idPago,
        montoUsd: p.montoUsd || 0,
        montoBs: p.montoBs || 0,
        tipoPago: p.tipoPago || 'No especificado',
        fechaPago: p.fechaPago,
        numeroRecibo: p.numeroRecibo || '',
        observaciones: p.observaciones || ''
      }));
    } else {
      pagosDocumento.value = pagosDelDoc;
    }

    form.value.idCliente = clienteInfo.idCliente || detalle.idCliente;
    form.value.tasa_bs_usd = tasaOriginal;

    const totalDoc = documentoSeleccionado.value.total_usd || 0;
    const pagadoCliente = montoPagadoReal || 0;
    
    let factorCliente = 1;
    if (esCreditoFinal && totalDoc > 0 && pagadoCliente > 0 && pagadoCliente < totalDoc) {
      factorCliente = pagadoCliente / totalDoc;
    }

    form.value.detalles = items.map((d, idx) => {
      const precioOriginal = d.precio_unitario_usd || 0;
      const precioCliente = precioOriginal * factorCliente;
      
      return {
        seleccionado: false,
        tipo_item: d.tipo_item, 
        idProducto: d.idProducto, 
        idServicio: d.idServicio, 
        idProductoServicios: d.idProductoServicios,
        nombre: d.nombre, 
        codigo: d.codigo,
        cantidad: d.cantidad, 
        cantidad_original: d.cantidad,
        precio_unitario_original: precioOriginal,
        precio_unitario_usd: precioCliente,
        precio_unitario_bs: precioCliente * tasaOriginal,
        factor_cliente: factorCliente,
        iva_porcentaje: 0, iva_usd: 0, iva_bs: 0,
        costo_unitario_usd: d.costo_unitario_usd || 0, 
        costo_total_usd: d.costo_total_usd || 0,
        ganancia_revertida_usd: d.ganancia_revertida_usd || 0,
        condicion_producto: 'bueno', 
        reingresa_inventario: false,
        observaciones: '', 
        orden: idx + 1, 
        descripcion_manual: d.descripcion_manual || ''
      };
    });

    cerrarModalBusqueda();
    toastSuccess('Documento cargado', `${doc.numero} — ${clienteInfo.nombre_completo}`);
  } catch (e) {
    console.error('Error:', e);
    toastError('Error al cargar documento', e.message || 'No se pudo cargar el detalle.');
  } finally {
    modalBusqueda.value.cargando = false;
  }
};

const redistribuirPreciosFaltantes = (items, totalDoc, factorProporcion = 1) => {
  if (!items.length || totalDoc <= 0) return items;

  const sumaActual = items.reduce(
    (s, it) => s + (it.precio_unitario_usd || 0) * (it.cantidad || 1),
    0
  );
  const faltante = totalDoc - sumaActual;

  if (faltante <= 0.01) return items;

  const sinPrecio = items.filter(it => !it.precio_unitario_usd || it.precio_unitario_usd === 0);
  const conPrecio = items.filter(it => it.precio_unitario_usd > 0);

  if (sinPrecio.length === 0) return items;

  if (conPrecio.length === 0) {
    const totalCantidad = items.reduce((s, it) => s + (it.cantidad || 1), 0) || items.length;
    items.forEach(it => {
      it.precio_unitario_usd = (totalDoc / totalCantidad) * factorProporcion;
    });
  } else {
    const totalCantidadSinPrecio =
      sinPrecio.reduce((s, x) => s + (x.cantidad || 1), 0) || sinPrecio.length;

    sinPrecio.forEach(it => {
      const proporcion = (it.cantidad || 1) / totalCantidadSinPrecio;
      const montoItem = faltante * proporcion;
      it.precio_unitario_usd = (montoItem / (it.cantidad || 1)) * factorProporcion;
    });
  }

  return items;
};

const extraerItems = async (doc, tipo) => {
  let detalles = [], subtotalDoc = 0, totalDoc = 0;

  if (tipo === 'venta') {
    detalles = doc.detalles || doc.detalleventa || doc.detalleVenta || [];
    subtotalDoc = doc.subtotalUsd || 0;
    totalDoc = doc.totalUsd || 0;
  } else if (tipo === 'servicio_externo') {
    detalles = doc.detalles || doc.servicioExternoDetalles || doc.servicioExternoDetalle || [];
    subtotalDoc = doc.subtotalUsd || 0;
    totalDoc = doc.totalUsd || doc.costo || 0;
  } else if (tipo === 'servicio_recepcion') {
    detalles = doc.detalles || doc.recepcionEquipoDetalles || doc.recepcionEquipoDetalle || [];
    subtotalDoc = doc.subtotalUsd || doc.precioTotalUsd || 0;
    totalDoc = doc.precioTotalUsd || doc.totalUsd || 0;
  }

  const factorProporcion = subtotalDoc > 0 ? (totalDoc / subtotalDoc) : 1;
  const items = [];

  for (const d of detalles) {
    let precioOriginal = d.precioUnitarioUsd
                      ?? d.precio_unitario_usd
                      ?? d.subtotalUsd
                      ?? d.precioUnitario
                      ?? d.precio_unitario
                      ?? d.precio
                      ?? d.costo
                      ?? 0;

    if ((!precioOriginal || precioOriginal === 0) && d.subtotalUsd && d.cantidad > 0) {
      precioOriginal = d.subtotalUsd / d.cantidad;
    }
    if ((!precioOriginal || precioOriginal === 0) && d.precioUnitarioBs && doc.tasaBsUsd > 0) {
      precioOriginal = d.precioUnitarioBs / doc.tasaBsUsd;
    }

    const cantidad = d.cantidad || 1;
    const precioAjustado = precioOriginal * factorProporcion;

    let nombre = d.nombreProducto
              || d.productoNombre
              || d.nombre
              || d.nombreServicio
              || d.descripcionManual
              || d.descripcion
              || d.tipoTrabajo
              || d.tipoReparacion
              || d.concepto;

    let codigo = d.productoCodigo
              || d.codigo
              || d.codigoProducto
              || d.codigoServicio
              || d.referencia
              || '';

    if ((!nombre || nombre.trim() === '' || nombre === 'Item') && d.idProducto) {
      const n = await obtenerNombreProducto(d.idProducto);
      if (n) { nombre = n; codigo = codigo || (cacheProductos.value[d.idProducto]?.codigo || ''); }
      else nombre = `Producto #${d.idProducto}`;
    }
    if ((!nombre || nombre.trim() === '') && d.idServicio) {
      const n = await obtenerNombreServicio(d.idServicio);
      if (n) { nombre = n; codigo = codigo || (cacheServicios.value[d.idServicio]?.codigo || ''); }
      else nombre = `Servicio #${d.idServicio}`;
    }
    if (!nombre || nombre.trim() === '') {
      nombre = d.observaciones || `Item #${d.idRecepcionEquipoDetalle || d.idServicioExternoDetalle || ''}`;
    }

    if (!codigo || codigo.trim() === '') {
      if (d.idProducto) codigo = `PROD-${d.idProducto}`;
      else if (d.idServicio) codigo = `SERV-${d.idServicio}`;
      else if (d.idRecepcionEquipoDetalle) codigo = `REC-${d.idRecepcionEquipoDetalle}`;
      else if (d.idServicioExternoDetalle) codigo = `EXT-${d.idServicioExternoDetalle}`;
      else codigo = '';
    }

    items.push({
      tipo_item: d.idProducto
        ? 'producto'
        : (d.idServicio ? 'servicio' : (d.idProductoServicios ? 'producto_servicio' : 'concepto_manual')),
      idProducto: d.idProducto,
      idServicio: d.idServicio,
      idProductoServicios: d.idProductoServicios,
      nombre, codigo, cantidad,
      precio_unitario_usd: precioAjustado,
      precio_unitario_bs: d.precioUnitarioBs || (precioAjustado * (doc.tasaBsUsd || 0)),
      iva_porcentaje: 0, iva_usd: 0, iva_bs: 0,
      costo_unitario_usd: d.costoUnitarioUsd || 0,
      costo_total_usd: d.costoTotalUsd || 0,
      ganancia_revertida_usd: d.gananciaTotalUsd || d.gananciaEstimadaUsd || 0,
      descripcion_manual: d.descripcionManual || d.observaciones || ''
    });
  }

  redistribuirPreciosFaltantes(items, totalDoc, factorProporcion);

  return items;
};

const extraerCuotas = (doc, tipo) => {
  if (tipo === 'venta' && doc.cuotasCredito) {
    return doc.cuotasCredito.map(c => ({ 
      idCuota: c.idCreditoCuota, 
      numeroCuota: c.numeroCuota, 
      fechaVencimiento: c.fechaVencimiento, 
      montoUsd: c.montoUsd || 0, 
      saldoUsd: c.saldoUsd || 0, 
      estado: c.estado || 'pendiente' 
    }));
  }
  
  if (tipo === 'servicio_externo') {
    const cuotasRaw = doc.creditoServicioExternos 
                   || doc.creditosServicioExterno 
                   || doc.creditos 
                   || doc.cuotasCredito 
                   || [];
    
    return cuotasRaw.map(c => ({ 
      idCuota: c.idCreditoServicioExterno || c.idCredito, 
      numeroCuota: c.numeroCuota, 
      fechaVencimiento: c.fechaVencimiento, 
      montoUsd: c.montoUsd || c.montoTotal || 0, 
      saldoUsd: c.saldoUsd || c.saldoPendiente || 0, 
      estado: c.estado || 'pendiente' 
    }));
  }
  
  if (tipo === 'servicio_recepcion') {
    const cuotas = [];
    const detalles = doc.detalles || doc.recepcionEquipoDetalles || doc.recepcionEquipoDetalle || [];
    
    for (const det of detalles) {
      const salidas = det.salidasEquipoDetalle || det.salidaEquipoDetalles || det.salidaEquipoDetalle || [];
      for (const salida of salidas) {
        const creditos = salida.creditosSalidaEquipo || salida.creditoSalidaEquipo || [];
        for (const c of creditos) {
          cuotas.push({
            idCuota: c.idCreditoSalidaEquipo,
            numeroCuota: c.numeroCuota,
            fechaVencimiento: c.fechaVencimiento,
            montoUsd: c.montoUsd || 0,
            saldoUsd: c.saldoPendienteUsd || 0,
            estado: c.estado || 'pendiente'
          });
        }
      }
    }
    
    return cuotas;
  }
  
  return [];
};

const extraerPagos = (doc, tipo) => {
  if (tipo === 'venta') {
    return (doc.pagos || doc.pagoVenta || []).map(p => ({ 
      idPago: p.idPago, 
      montoUsd: p.montoUsd || 0, 
      montoBs: p.montoBs || 0, 
      fechaPago: p.fechaPago, 
      tipoPago: p.tipoPago, 
      numeroRecibo: p.numeroRecibo, 
      observaciones: p.observaciones 
    }));
  }
  if (tipo === 'servicio_externo') {
    return (doc.pagos || doc.pagoServicioExternos || doc.pagoServicioExterno || []).map(p => ({ 
      idPago: p.idPagoServicioExterno || p.idPago, 
      montoUsd: p.montoUsd || 0, 
      montoBs: p.montoBs || 0, 
      fechaPago: p.fechaPago, 
      tipoPago: p.metodoPago || p.tipoPago, 
      numeroRecibo: p.referencia || p.numeroRecibo, 
      observaciones: p.observaciones 
    }));
  }
  return [];
};

const deseleccionarDocumento = () => {
  documentoSeleccionado.value = null;
  form.value.detalles = [];
  form.value.idCliente = null;
  pagosDocumento.value = [];
};

const volverAListado = () => router.push('/devoluciones');

const guardar = async () => {
  if (!puedeGuardar.value) {
    if (!documentoSeleccionado.value) {
      toastWarning('Falta documento', 'Selecciona un documento de origen antes de continuar.');
    } else if (!form.value.motivo) {
      toastWarning('Falta motivo', 'Indica el motivo de la devolución.');
    } else if (itemsSeleccionados.value.length === 0) {
      toastWarning('Sin items', 'Selecciona al menos un item a devolver.');
    } else if (excedeMontoMaximo.value) {
      toastWarning('Monto excedido', `El monto a devolver supera el máximo permitido ($${formatMoney(montoMaximoDevolver.value)}).`);
    }
    return;
  }

  const detallesCalculados = itemsSeleccionados.value.map(d => ({
    tipo_item: d.tipo_item, idProducto: d.idProducto, idServicio: d.idServicio, idProductoServicios: d.idProductoServicios,
    descripcion_manual: d.descripcion_manual, cantidad: d.cantidad,
    precio_unitario_usd: d.precio_unitario_usd, precio_unitario_bs: d.precio_unitario_bs,
    iva_porcentaje: 0, iva_usd: 0, iva_bs: 0,
    costo_unitario_usd: d.costo_unitario_usd, costo_total_usd: d.costo_total_usd,
    ganancia_revertida_usd: d.ganancia_revertida_usd,
    condicion_producto: d.condicion_producto, reingresa_inventario: d.reingresa_inventario,
    observaciones: d.observaciones, orden: d.orden
  }));

  guardando.value = true;
  try {
    const payload = {
      tipo_origen: form.value.tipo_origen,
      id_documento_origen: documentoSeleccionado.value.id,
      idCliente: form.value.idCliente,
      motivo: form.value.motivo, descripcion: form.value.descripcion, observaciones: form.value.observaciones,
      tasa_bs_usd: form.value.tasa_bs_usd, tipo_reembolso: form.value.tipo_reembolso,
      afecta_inventario: form.value.afecta_inventario, afecta_caja: form.value.afecta_caja,
      idUsuario_registra: usuario?.idUsuario,
      detalles: detallesCalculados
    };

    if (form.value.tipo_origen === 'venta') payload.idVenta = documentoSeleccionado.value.id;
    else if (form.value.tipo_origen === 'servicio_externo') payload.idServicioExterno = documentoSeleccionado.value.id;
    else if (form.value.tipo_origen === 'servicio_recepcion') payload.tipo_origen = 'servicio_externo';

    await crearDevolucion(payload);
    toastSuccess('¡Devolución creada!', 'El registro se guardó correctamente.');
    setTimeout(() => router.push('/devoluciones'), 1200);
  } catch (error) {
    console.error(error);
    toastError('Error al guardar', error.message || 'Ocurrió un problema inesperado.');
  } finally { guardando.value = false; }
};

watch(metodoPagoPrincipal, (nuevo) => {
  if (!nuevo || form.value.tipo_reembolso) return;
  
  const tipo = nuevo.tipo.toLowerCase();
  if (tipo.includes('efectivo')) form.value.tipo_reembolso = 'efectivo';
  else if (tipo.includes('transferencia')) form.value.tipo_reembolso = 'transferencia';
  else if (tipo.includes('tarjeta') || tipo.includes('punto')) form.value.tipo_reembolso = 'transferencia';
  else if (tipo.includes('credito') || tipo.includes('crédito')) form.value.tipo_reembolso = 'credito_cuenta';
});

const formatMoney = (v) => {
  const num = Number(v || 0);
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-VE') : '—';

const getEstadoColor = (estado) => {
  const e = normalizar(estado);
  if (['completada', 'pagada', 'activa', 'pagado', 'entregado', 'entregada', 'finalizado', 'finalizada', 'terminado'].includes(e)) return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
  if (['pendiente', 'parcial', 'pordiagnosticar', 'enproceso'].includes(e)) return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
  if (['anulada', 'cancelado', 'rechazada', 'imposible'].includes(e)) return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
  return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
};

onMounted(() => cargarPreviewNumero());
</script>

<style scoped>
.text-lexend { font-family: 'Lexend', sans-serif; }
@keyframes zoomIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
.animate-zoomIn { animation: zoomIn 0.2s ease-out; }
.animate-fadeIn { animation: fadeIn 0.3s ease-out; }
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

@keyframes toastProgress {
  from { width: 100%; }
  to { width: 0%; }
}

@keyframes toastIconBounce {
  0% { transform: scale(0.5) rotate(-10deg); opacity: 0; }
  50% { transform: scale(1.15) rotate(5deg); opacity: 1; }
  100% { transform: scale(1) rotate(0deg); opacity: 1; }
}
.animate-toastIcon { animation: toastIconBounce 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55); }

.custom-scrollbar::-webkit-scrollbar { width: 6px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
.dark .custom-scrollbar::-webkit-scrollbar-thumb { background: #334155; }
.dark select option { background-color: #1f2937; color: #ffffff; }
</style>